import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { ArrowLeft, Users, ChevronRight, RefreshCw, Plus } from 'lucide-react';
import { API_URL as BACKEND_URL } from '../config/api';
import { useAuth } from '../context/CompleteAuthContext';

const LegacyGroupsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/groups`, { credentials: 'include' });
        if (r.ok) setGroups(await r.json());
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, [isAuthenticated, isLoading, navigate]);

  const openGroup = (groupId) => {
    try { sessionStorage.setItem('open_group_id', groupId); } catch { /* ignore */ }
    navigate('/');
  };

  const goCreate = () => {
    try { sessionStorage.setItem('legacy_create_group', '1'); } catch { /* ignore */ }
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 py-3 sm:py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="legacy-groups-back" className="h-9 w-9">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className="text-base sm:text-lg font-semibold text-gray-900">Legacy Groups</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-4 p-4 rounded-xl bg-blue-50/60 border border-blue-100">
          <p className="text-sm text-gray-700">
            <span className="font-medium">Heads up.</span> Groups have been replaced by <span className="font-medium">Quick Splits</span>{' '}
            for the launch experience. We've kept your existing groups accessible here. New activity should use Quick Splits from the dashboard.
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">Your groups</CardTitle>
              <Button size="sm" variant="outline" onClick={goCreate} data-testid="legacy-create-group-btn">
                <Plus className="w-4 h-4 mr-1" />
                New
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading ? (
              <div className="text-center py-8 text-gray-500">
                <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                <p className="text-sm">Loading…</p>
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-10 text-gray-500" data-testid="legacy-groups-empty">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium text-gray-700">No legacy groups</p>
                <p className="text-sm mt-1">Use Quick Splits from your dashboard.</p>
              </div>
            ) : (
              groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => openGroup(group.id)}
                  data-testid={`legacy-group-${group.id}`}
                  className="w-full text-left p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10 shrink-0">
                      <AvatarFallback className="bg-blue-100 text-blue-700 font-semibold">
                        {group.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 truncate">{group.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {group.members?.length || 0} member{(group.members?.length || 0) !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                      {group.description && (
                        <p className="text-sm text-gray-500 truncate">{group.description}</p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LegacyGroupsPage;
